import { useEffect, useState, useMemo } from "react";
import {
  Plus, Search, Loader2, Phone, MessageSquare, CheckCircle2, Trash2, BellRing,
  Clock, Mail, CalendarPlus, Repeat, XCircle, MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import apiClient from "./api";
import ReminderModal from "./ReminderModal";
import RescheduleReminderModal from "./RescheduleReminderModal";
import ConvertReminderModal from "./ConvertReminderModal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STATUS_COLOR = {
  pending: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  snoozed: "bg-[#8B73FF]/15 text-[#C9BCFF] border-[#8B73FF]/40",
  done: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-white/10 text-white/50 border-white/20",
};

function dueLabel(remind_on) {
  try {
    const dt = parseISO(remind_on);
    const diff = differenceInCalendarDays(dt, new Date());
    if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, color: "text-red-400" };
    if (diff === 0) return { text: "Due today", color: "text-[#FC5106]" };
    if (diff === 1) return { text: "Due tomorrow", color: "text-[#C9BCFF]" };
    return { text: `In ${diff} days`, color: "text-white/60" };
  } catch {
    return { text: remind_on, color: "text-white/60" };
  }
}

function whatsappContactUrl(r) {
  if (!r.customer_phone) return null;
  const phone = r.customer_phone.replace(/\D/g, "");
  const num = phone.length === 10 ? `91${phone}` : phone;
  const text = encodeURIComponent(
    `Hi ${r.customer_name}, this is BarknFurs Grooming Studio in Bhubaneswar. Just following up on your enquiry${r.service ? ` about ${r.service}` : ""}. Let us know how we can help — call us at +91 88833 39158.`
  );
  return `https://wa.me/${num}?text=${text}`;
}

export default function AdminReminders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("active");
  const [modal, setModal] = useState(false);
  const [reschedule, setReschedule] = useState(null); // { reminder, presetReason }
  const [convert, setConvert] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter === "active") params.status = "pending";
      if (filter === "due_today_or_overdue") params.due = "due_today_or_overdue";
      if (filter === "upcoming") params.due = "upcoming";
      if (filter === "done") params.status = "done";
      const { data } = await apiClient.get("/api/admin/reminders", { params });
      setItems(data);
    } catch {
      toast.error("Could not load reminders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter]);

  const updateStatus = async (r, status, extra = {}) => {
    try {
      const { data } = await apiClient.patch(`/api/admin/reminders/${r.id}`, {
        status, ...extra,
      });
      setItems((arr) => arr.map((x) => (x.id === r.id ? data : x)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete reminder for ${r.customer_name}?`)) return;
    try {
      await apiClient.delete(`/api/admin/reminders/${r.id}`);
      setItems((arr) => arr.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const dueCount = useMemo(
    () =>
      items.filter((r) => {
        try {
          return r.status === "pending" && parseISO(r.remind_on) <= new Date();
        } catch {
          return false;
        }
      }).length,
    [items]
  );

  return (
    <div className="space-y-6" data-testid="admin-reminders">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Reminders</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            Customer follow-ups
          </h1>
          <p className="text-white/50 text-sm mt-1">
            {items.length} reminder{items.length !== 1 ? "s" : ""}
            {dueCount > 0 && (
              <span className="ml-2 text-[#FC5106] font-semibold">· {dueCount} due now</span>
            )}
          </p>
        </div>
        <button onClick={() => setModal(true)} data-testid="add-reminder-button" className="btn-primary !py-2.5 !px-4 !text-sm">
          <Plus className="w-4 h-4" /> New reminder
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-4 grid md:grid-cols-12 gap-3">
        <div className="md:col-span-8 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="reminders-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, breed, service…"
            className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none"
          />
        </div>
        <div className="md:col-span-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="reminders-filter" className="bg-black/50 border-white/10 text-sm text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="active">Active (pending)</SelectItem>
              <SelectItem value="due_today_or_overdue">Due today or overdue</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="done">Completed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <BellRing className="w-10 h-10 text-white/50 mx-auto mb-3" />
            <p className="text-white/50 text-sm mb-4">No reminders yet.</p>
            <button onClick={() => setModal(true)} className="btn-secondary !py-2.5 !px-4 !text-sm">
              <Plus className="w-4 h-4" /> Add your first reminder
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-white/5">
            {items.map((r) => {
              const due = dueLabel(r.remind_on);
              const wa = whatsappContactUrl(r);
              const isPending = r.status === "pending";
              return (
                <li
                  key={r.id}
                  data-testid={`reminder-row-${r.id}`}
                  className="px-5 py-4 hover:bg-white/[0.02] transition"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="text-white font-semibold">{r.customer_name}</p>
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[r.status]}`}>
                          {r.status}
                        </span>
                        {isPending && (
                          <span className={`text-[11px] font-semibold flex items-center gap-1 ${due.color}`}>
                            <Clock className="w-3 h-3" /> {due.text}
                          </span>
                        )}
                      </div>
                      <p className="text-white/50 text-xs">
                        {r.customer_phone || r.customer_email || "—"}
                        {r.service ? ` · ${r.service}` : ""}
                        {r.pet_name ? ` · ${r.pet_name}` : ""}
                        {r.pet_breed ? ` (${r.pet_breed})` : ""}
                      </p>
                      {r.lead_source && (
                        <div className="mt-1.5">
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#8B73FF]/10 border border-[#8B73FF]/30 text-[#C9BCFF]">
                            From · {r.lead_source}
                          </span>
                        </div>
                      )}
                      {r.notes && (
                        <p className="text-white/65 text-sm mt-2 whitespace-pre-line">{r.notes}</p>
                      )}
                      <p className="text-white/50 text-[10px] mt-1.5 uppercase tracking-wider">
                        Remind on {(() => { try { return format(parseISO(r.remind_on), "PPP"); } catch { return r.remind_on; } })()}
                        {r.kind === "monthly_followup" && <> · <span className="text-[#C9BCFF]">Auto · Monthly groom follow-up</span></>}
                        {r.linked_booking_id && <> · Booking #{r.linked_booking_id.slice(0, 8)}</>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {r.customer_phone && (
                        <a
                          href={`tel:${r.customer_phone}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                          aria-label="Call"
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {wa && (
                        <a
                          href={wa}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25"
                          aria-label="WhatsApp"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {r.customer_email && (
                        <a
                          href={`mailto:${r.customer_email}`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"
                          aria-label="Email"
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button
                        onClick={() => remove(r)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/50 hover:bg-red-500/15 hover:text-red-300"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Follow-up actions */}
                  {isPending && (
                    <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-1">
                      <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1">Follow-up:</span>
                      <button
                        onClick={() => setConvert(r)}
                        data-testid={`reminder-confirm-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-xs font-semibold transition"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Confirmed → Book
                      </button>
                      <button
                        onClick={() => setReschedule({ reminder: r, presetReason: "No response — try again later." })}
                        data-testid={`reminder-noresponse-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FC5106]/15 border border-[#FC5106]/40 text-[#FC5106] hover:bg-[#FC5106]/25 text-xs font-semibold transition"
                      >
                        <Repeat className="w-3.5 h-3.5" /> No response → Reschedule
                      </button>
                      <button
                        onClick={() => setReschedule({ reminder: r, presetReason: "Customer asked to remind later." })}
                        data-testid={`reminder-later-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#8B73FF]/15 border border-[#8B73FF]/40 text-[#C9BCFF] hover:bg-[#8B73FF]/25 text-xs font-semibold transition"
                      >
                        <CalendarPlus className="w-3.5 h-3.5" /> Remind me later
                      </button>
                      <button
                        onClick={() => updateStatus(r, "cancelled")}
                        data-testid={`reminder-cancel-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80 text-xs font-semibold transition"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Not interested
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ReminderModal
        open={modal}
        onClose={() => setModal(false)}
        onCreated={(r) => setItems((arr) => [r, ...arr])}
      />
      <RescheduleReminderModal
        open={!!reschedule}
        reminder={reschedule?.reminder}
        presetReason={reschedule?.presetReason}
        onClose={() => setReschedule(null)}
        onSaved={(updated) => setItems((arr) => arr.map((x) => (x.id === updated.id ? updated : x)))}
      />
      <ConvertReminderModal
        open={!!convert}
        reminder={convert}
        onClose={() => setConvert(null)}
        onConverted={() => {
          // Refresh list — converted reminder is now done
          load();
        }}
      />
    </div>
  );
}
